import { X } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconButton } from "../IconButton/IconButton";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Fades the first and last 24px of the scrolling body so a cut-off row doesn't end in a hard edge under the header or above the actions. */
const FADE_TOP = "[mask-image:linear-gradient(to_bottom,transparent,#000_1.5rem)]";
const FADE_BOTTOM = "[mask-image:linear-gradient(to_bottom,#000_calc(100%-1.5rem),transparent)]";
const FADE_BOTH = "[mask-image:linear-gradient(to_bottom,transparent,#000_1.5rem,#000_calc(100%-1.5rem),transparent)]";

function fadeClass(above: boolean, below: boolean) {
  if (above && below) return FADE_BOTH;
  if (above) return FADE_TOP;
  return below ? FADE_BOTTOM : "";
}

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;

  actions?: ReactNode;

  "aria-label"?: string;

  /** Accessible name of the close button. */
  closeLabel?: string;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  actions,
  "aria-label": ariaLabel,
  closeLabel = "Close",
}: Readonly<ModalProps>) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [moreAbove, setMoreAbove] = useState(false);
  const [moreBelow, setMoreBelow] = useState(false);

  const updateFades = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    setMoreAbove(body.scrollTop > 1);
    setMoreBelow(body.scrollHeight - body.scrollTop - body.clientHeight > 1);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: children changes the body's content, so re-measure.
  useEffect(() => {
    const body = bodyRef.current;
    if (!open || !body) return;
    updateFades();
    const observer = new ResizeObserver(updateFades);
    observer.observe(body);
    for (const child of body.children) observer.observe(child);
    return () => observer.disconnect();
  }, [open, children, updateFades]);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    const focusable = dialog?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    (focusable?.[0] ?? dialog)?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const nodes = dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <div aria-hidden="true" className="fixed inset-0 z-[100] bg-surface-overlay" onClick={onClose} />
      <div className="pointer-events-none fixed inset-0 z-[101] flex items-start justify-center p-6 pt-[10vh]">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={title ? undefined : ariaLabel}
          aria-labelledby={title ? titleId : undefined}
          tabIndex={-1}
          className="pointer-events-auto flex max-h-[80vh] w-full max-w-md flex-col rounded-2xl border border-border-input bg-surface-elevated shadow-xl animate-modal-in"
        >
          {title && (
            <div className="flex items-start gap-3 px-6 pt-6">
              <span id={titleId} className="block min-w-0 flex-1 text-content-primary [font:var(--type-h3)]">
                {title}
              </span>
              <IconButton variant="ghost" size="md" aria-label={closeLabel} icon={<X />} onClick={onClose} />
            </div>
          )}
          <div
            ref={bodyRef}
            onScroll={updateFades}
            className={[
              "min-h-0 flex-1 overflow-y-auto px-6",
              title ? "mt-4" : "mt-6",
              actions ? "" : "pb-6",
              fadeClass(moreAbove, moreBelow),
            ].join(" ")}
          >
            {children}
          </div>
          {actions && <div className="flex flex-wrap items-center justify-end gap-2 px-6 mt-4 pb-6">{actions}</div>}
        </div>
      </div>
    </>,
    document.body,
  );
}
